import React, {useContext} from 'react';
import {Typography} from '@mui/material';
import ImageNotAvailablePlaceholder from '../ImageNotAvailablePlaceholder';
import InstantApprovalChip from './InstantApprovalChip';
import RequestSpaceChip from './RequestSpaceChip';
import AtCapacityChip from './AtCapacityChip';
import LocationListItemImages from './LocationImages/LocationListItemImages';
import LocationFeatureList from './LocationFeatureList';
import LocationTrustSignals from '../features/LocationTrustSignals';
import './style.css'
import LocationImageNav from './LocationImages/LocationImageNav';
import NotLiveLocationChip from "../../NotLiveLocationChip";
import LocationFavoriteIcon from '../LocationFavoriteIcon';
import {AppContext} from "../../../context/app-context";
import {isBuyerAccountType} from '../../../util/AccountUtils';
import Money from 'js-money';
import type {Supplier} from '../../../types/Supplier';
import styled from "@mui/system/styled";
import {useImageCarousel} from "../../../hooks/location/useImageCarousel";
import {getLocationGallery} from "../../../util/LocationMediaUtils";
import {useFeatureFlags} from "../../../hooks/routing/useFeatureFlags";
import {useLocationTrustSignals} from "../../../hooks/location/useLocationTrustSignals";

interface LocationListItemMapPreviewProps {
  location: Supplier;
  showVisibilityChip: boolean;
  isFavorite?: boolean;
  onContentClick?: () => void;
}

const MapPreviewCard = styled('div')(() => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'white',
  overflow: 'hidden',
  margin: 0,
  padding: 0,
  border: '1px solid #E9E9FB',
  borderRadius: '8px',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
    borderColor: '#3F51B5',
  },
}));

const ImageContainer = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  paddingTop: '62%',
  overflow: 'hidden',
  backgroundColor: '#f4f4f8',
}));

const ImageInner = styled('div')(() => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  '& > div': {
    width: '100%',
    height: '100%',
  },
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
}));

const ContentSection = styled('div')(() => ({
  padding: '12px 12px 16px 12px',
  cursor: 'pointer',
}));

const HeaderRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
}));

const TitleRow = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px',
}));

const Title = styled(Typography)(() => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  fontFamily: 'Inter',
  color: '#000',
}));

const FeaturesSection = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  marginBottom: '8px',
}));

const PricingSection = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-end',
  marginTop: '16px',
  paddingTop: '12px',
  borderTop: '1px solid #E0E0E0',
}));

const PriceBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

const PriceAmount = styled(Typography)(() => ({
  fontSize: '1.75rem',
  fontWeight: 600,
  fontFamily: 'Inter',
  color: '#000',
}));

const PriceUnit = styled('span')(() => ({
  fontSize: '1rem',
  fontWeight: 400,
}));

const LocationListItemMapPreview: React.FC<LocationListItemMapPreviewProps> = (props) => {
  const appContext = useContext(AppContext);
  const {location, showVisibilityChip, isFavorite, onContentClick} = props;
  const {supplierCapacitySchedule} = useFeatureFlags();
  const isAtCapacity = supplierCapacitySchedule && location.hasAvailableCapacityToday === false;
  const listingImage = location.listingImageFileName;
  const gallery = getLocationGallery(location);

  // Total images = listing image (if exists) + gallery images
  const totalImages = (listingImage ? 1 : 0) + gallery.length;

  const {activeSlide, prevImage, nextImage} = useImageCarousel({totalImages});

  const {locationName, city, state} = location;
  const locationDescription = locationName || `${city}, ${state}`;

  // Calculate pricing
  const currency = Money.USD;
  const pricePerMonth = location.pricePerMonth || 0;
  const pricePerDay = location.pricePerDay || 0;
  const moneyPricePerMonth = new Money(pricePerMonth, currency);
  const moneyPricePerDay = new Money(pricePerDay, currency);

  const formattedMonthly = moneyPricePerMonth.isPositive() ? `${currency.symbol}${moneyPricePerMonth}` : '';
  const formattedDaily = moneyPricePerDay.isPositive() ? `${currency.symbol}${moneyPricePerDay}` : '';

  // Same selection as the search list card (LocationListItem), so the two never show different features.
  const {trustSignals, otherFeatures} = useLocationTrustSignals(location.features, location.featureDetails);


  return (
    <MapPreviewCard>
      {/* Image Section */}
      <ImageContainer>
        <ImageInner>
          {
            listingImage ?
              <LocationListItemImages
                activeSlide={activeSlide}
                listingImageFileName={listingImage}
                gallery={gallery}
                locationId={location.locationId}
                height="100%"
                width="100%"
                borderRadius="0"
                objectfit="cover"
              /> :
              <ImageNotAvailablePlaceholder
                height="100%"
                width="100%"
                borderRadius="0"
              />
          }
          <LocationImageNav activeSlide={activeSlide} totalImages={totalImages} onPrev={prevImage} onNext={nextImage}/>
        </ImageInner>
      </ImageContainer>

      {/* Content Section */}
      <ContentSection onClick={onContentClick}>
        {/* Approval Status */}
        <HeaderRow>
          <div>
            {isAtCapacity ? (
              <AtCapacityChip/>
            ) : location.hasInstantApproval ? (
              <InstantApprovalChip/>
            ) : (
              <RequestSpaceChip/>
            )}
            {showVisibilityChip && !location.visible && <NotLiveLocationChip/>}
          </div>
        </HeaderRow>

        {/* Title & Favorite */}
        <TitleRow>
          <Title>{locationDescription}</Title>
          {isBuyerAccountType(appContext?.user?.type) && (
            <LocationFavoriteIcon locationId={location.locationId} isFavorite={isFavorite}/>
          )}
        </TitleRow>

        {/* Features */}
        <FeaturesSection>
          <LocationTrustSignals signals={trustSignals}/>
          <LocationFeatureList maxFeatures={4} features={otherFeatures}/>
        </FeaturesSection>

        {/* Pricing */}
        <PricingSection>
          <PriceBlock>
            <PriceAmount>
              {formattedMonthly || formattedDaily}
              <PriceUnit>
                /{formattedMonthly ? 'mon' : 'day'}
              </PriceUnit>
            </PriceAmount>
          </PriceBlock>
          <PriceBlock style={{textAlign: 'right'}}>
            {formattedMonthly && formattedDaily && (
              <PriceAmount>
                {formattedDaily}
                <PriceUnit>/day</PriceUnit>
              </PriceAmount>
            )}
          </PriceBlock>
        </PricingSection>
      </ContentSection>
    </MapPreviewCard>
  );
};

export default LocationListItemMapPreview;
